import { computed } from 'nanostores'

import { EARLIEST_MODIFIABLE_WEEK } from '@/constants/program-dates'
import type { CampaignWeek } from '@/models/campaign-week'
import dayjs, { type Dayjs } from '@/utilities/dayjs-config'
import { getWeeksArray, isWeekAfterCutoff } from '@/utilities/sakara'

import { $campaignWeeks, $skippedWeeks, $subscription } from './stores'

export const $pastSkippedWeeks = computed([$skippedWeeks], (skippedWeeks) => {
  return skippedWeeks.filter(
    (skippedWeek) => !isWeekAfterCutoff(dayjs(skippedWeek)),
  )
})

export const $lockedInCampaignWeek = computed(
  [$campaignWeeks],
  (campaignWeeks) => {
    const campaignWeek =
      campaignWeeks[EARLIEST_MODIFIABLE_WEEK.format('YYYY-MM-DD')]

    if (campaignWeek?.campaignWeekNumber > 1) {
      return campaignWeek
    }

    return null
  },
)

// If the next modifiable subscription week is opted into a campaign and it is not the first week
// of the campaign, return true because the customer is locked in.
export const $isCurrentlyLockedInToCampaign = computed(
  [$lockedInCampaignWeek],
  (lockedInCampaignWeek) => !!lockedInCampaignWeek,
)

export const $isWeekInSubscription = computed(
  [$subscription],
  (subscription) => (week: Dayjs) =>
    week.isSameOrAfter(subscription?.startDate, 'week'),
)

export const $isCampaignOptedIn = (id: number) =>
  computed([$campaignWeeks], (campaignWeeks) =>
    Object.values(campaignWeeks).some(
      (campaignWeek) => campaignWeek.campaignId === id,
    ),
  )

export const $getCampaignWeeksById = (id: number) =>
  computed([$campaignWeeks], (campaignWeeks) =>
    Object.values(campaignWeeks).filter(
      (campaignWeek) => campaignWeek.campaignId === id,
    ),
  )

export const $getCampaignWeeksForWeeks = computed(
  [$campaignWeeks],
  (campaignWeeks) => (weeks: Dayjs[]) =>
    weeks.reduce((campaignWeeksResult, week) => {
      const campaignWeek = campaignWeeks[week.format('YYYY-MM-DD')]

      if (campaignWeek) {
        campaignWeeksResult.push(campaignWeek)
      }

      return campaignWeeksResult
    }, [] as CampaignWeek[]),
)

/**
 * Returns an array of weeks that are affected by changes
 * made to the weeks array param
 *
 * @param {dayjs[]} weeks An ordered array of weeks (ascending)
 * @returns dayjs[]
 */

export const $getAffectedWeeks = (weeks: Dayjs[]) =>
  computed([$campaignWeeks], (campaignWeeks) => {
    const startWeek = weeks[0]
    let affectedNumberOfWeeks = weeks.length

    // Loop backwards through weeks array to find the latest sytem/campaign week
    for (let i = weeks.length - 1; i >= 0; i -= 1) {
      const week = weeks[i]
      const campaignWeek = campaignWeeks[week.format('YYYY-MM-DD')]

      if (campaignWeek) {
        const endWeek = dayjs(campaignWeek.campaignStartWeek).add(
          campaignWeek.campaign.programLength,
          'weeks',
        )
        affectedNumberOfWeeks = endWeek.diff(startWeek, 'weeks')
        break
      }
    }

    return getWeeksArray(startWeek, affectedNumberOfWeeks)
  })
